import { type FormEvent, useState } from "react";
import { authApi } from "../lib/api/auth.js";

/**
 * Passwordless sign-in: enter an email, get a magic link. Used for both login and
 * invite-signup (pass the invite `code`). Always shows a generic "check your email"
 * on success — the server never reveals whether the address is eligible.
 */
export function EmailSignInForm({
  inviteCode,
  cta = "Email me a sign-in link",
}: {
  inviteCode?: string;
  cta?: string;
}) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await authApi.requestEmailLink(email.trim(), inviteCode);
    setBusy(false);
    if (res.success) setSent(true);
    else setError(res.error ?? "Couldn't send the link — check the address and try again.");
  }

  if (sent) {
    return (
      <output className="login__sent">
        Check your inbox — if <strong>{email.trim()}</strong> can sign in, a link is on its way. It
        expires in 20 minutes.
      </output>
    );
  }

  return (
    <form className="login__email" onSubmit={submit}>
      <input
        type="email"
        required
        autoComplete="email"
        placeholder="you@email.com"
        aria-label="email"
        className="login__input"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <button
        type="submit"
        className="btn btn-primary"
        disabled={busy || email.trim().length === 0}
      >
        {busy ? "Sending…" : cta}
      </button>
      {error && (
        <p className="login__hint" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
